// Components
import QuestionCheckboxes from 'app/components/tests/questions/QuestionCheckboxes';
import QuestionSelect from 'app/components/tests/questions/QuestionSelect';
import QuestionInput from 'app/components/tests/questions/QuestionInput';
import RadioOptions from 'app/components/tests/questions/RadioOptions';
import ResultExplain from 'app/components/tests/result/ResultExplain';
import ResultDisplay from 'app/components/tests/result/ResultDisplay';
import { AudioButton } from 'app/components/tests/media/AudioButton';
import ImageAudio from 'app/components/tests/media/ImageAudio';

// Constants and Types
import * as testConst from 'app/constants/tests';
import { checkIsCorrect } from 'app/utils/test';
import { AppStoreState } from 'app/redux/store';
import { Group, Question } from 'types/test';

import { useTranslation } from 'next-i18next';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';

interface QuestionItemProps {
  question: Question;
  group: Group;
  questionResult: Question | null;
  handleChange: (value: string | null, question: Question) => void;
  confirmIsReady: () => void;
  audioPlayer: React.RefObject<any>;
}

const QuestionItem = ({
  question,
  group,
  questionResult,
  handleChange,
  confirmIsReady,
  audioPlayer
}: QuestionItemProps) => {
  const { t } = useTranslation();
  const { process } = useSelector((state: AppStoreState) => state.test);

  // Determine question title
  const defaultTitle = group?.group_type == testConst.QUESTION_RADIO ? 'Mark your answer on your answer sheet.' : '';
  const showTitle = !(testConst.TOEIC_P2 === group?.part_id && process !== 'complete');
  const title = showTitle ? (question.title || defaultTitle) : '';

  const classResult = useMemo(() => {
    if (process !== 'complete' || !question.answer) {
      return '';
    }

    const { isCorrect } = checkIsCorrect({ ...question, result: questionResult?.result || '' });
    return isCorrect ? 'is-valid' : 'is-invalid';
  }, [process]);

  return (
    <div className="box mb-2" id={`q${question.id}`}>
      <div className={"box-header with-border pb-2"}>
        <b className={`py-0 my-3 pr-2 ${question.activeClass ? 'active-question' : ''}`}>
          <b>{t('tests.question')} {question?.number}: </b>
          <span className='pb-2' dangerouslySetInnerHTML={{ __html: title }} />
          <AudioButton timeAudio={question.time_audio} audioRef={audioPlayer} />
          <ResultDisplay groupType={group.group_type} questionResult={questionResult} />
        </b>

        <QuestionInput
          groupType={group.group_type}
          question={{ ...question, result: questionResult?.result || '' }}
          className={classResult}
          confirmIsReady={confirmIsReady}
          handleChange={handleChange}
        />

        <QuestionSelect
          groupType={group.group_type}
          question={{ ...question, result: questionResult?.result || '' }}
          className={classResult}
          confirmIsReady={confirmIsReady}
          handleChange={handleChange}
          options={group?.options?.split('//') || []}
        />

        <QuestionCheckboxes
          groupType={group.group_type}
          question={{ ...question, result: questionResult?.result || '' }}
          confirmIsReady={confirmIsReady}
          handleChange={handleChange}
          options={group?.options?.split('//') || []}
        />
      </div>

      <div className="box-body">
        <ImageAudio
          width={300}
          data={question}
          hide={false}
        />
        <RadioOptions
          groupType={group.group_type}
          partId={group.part_id}
          question={{ ...question, result: questionResult?.result || '' }}
          confirmIsReady={confirmIsReady}
          handleChange={handleChange}
          showAds={!!question?.number && question.number % 4 === 1}
        />
        <ResultExplain questionResult={questionResult} />
      </div>
    </div>
  );
};

export default QuestionItem;
